// Level: load, start/stop, win/fail, events from physics, and effects
import Matter from 'matter-js';
import { Registry } from '../core/registry';
import { Physics } from '../core/physics';
import { GFX } from '../core/graphics';
import { SFX } from '../core/audio';
import { $ } from './dom';
import { G, LEVELS, WORLDS, show, W, levelKey, type Particle } from './state';
import type { FxApi, Placement, SimEvent, SoundType } from '../core/types';
import { Store } from '../core/store';
import { buildTools, updateHud, handHint, hideHand } from './hud';
import { updatePartMenu } from './input';
import { openIntro } from './intro';
import { showWin } from './win';

export function loadLevel(li: number) {
  G.token = (G.token || 0) + 1; $('#ovWin').hidden = true;
  G.li = li; G.level = LEVELS[li]; G.mapW = G.level.w || 0; GFX.setWorld(G.level.wk); SFX.track(WORLDS[G.level.w || 0].k); G.pl = []; G.sel = -1; G.drag = null; G.hintStep = 0; G.fails = 0; G.fx = []; G.state = 'build';
  const p = Store.prof();
  const key = levelKey(li);
  if (p && p.fails && p.fails[key]) G.fails = p.fails[key];
  rebuild();
  show('game');
  $('#lvlNo').textContent = String((G.level.wi || 0) + 1);
  $('#infoBtn').hidden = !G.level.intro;
  buildTools(); updateHud();
  if (G.level.intro && p && !(p.intros || {})[G.level.intro]) { p.intros = p.intros || {}; p.intros[G.level.intro] = 1; Store.save(); openIntro(G.level.intro); }
  handHint();
}

function snappedPl() {
  const out: Placement[] = G.pl.map(p => ({ ...p }));
  for (let i = 0; i < out.length; i++) out[i] = Physics.snap(G.level, out, i);
  return out;
}
export function rebuild() { G.S = Physics.create(Matter, G.level, snappedPl()); }

// ---------- Start / stop ----------
$('#playBtn').addEventListener('click', () => {
  SFX.init();
  if (G.state === 'build') startPlay(); else if (G.state === 'play') stopPlay();
});
export function startPlay() {
  G.sel = -1; updatePartMenu(); hideHand();
  rebuild(); G.S.start(); G.state = 'play'; G.everPlayed = true; G.acc = 0; G.playT = 0;
  SFX.play('start'); updateHud();
}
function stopPlay() { G.state = 'build'; rebuild(); updateHud(); }

function onFail() {
  G.state = 'failing'; G.doneT = G.t;
  G.S.heroes.forEach(h => { h.sad = true; });
  G.fails++;
  const p = Store.prof(); if (p) { p.fails = p.fails || {}; p.fails[levelKey(G.li)] = G.fails; Store.save(); }
  SFX.play('fail');
  setTimeout(() => { if (G.state === 'failing') { G.state = 'build'; rebuild(); updateHud(); } }, 1300);
}
function onWin() {
  G.state = 'won'; G.doneT = G.t;
  const used = G.pl.length, stars = Physics.starsFor(G.level, used);
  const p = Store.prof();
  if (p) {
    const key = levelKey(G.li), old = p.prog[key] || { stars: 0, duck: false };
    p.prog[key] = { stars: Math.max(old.stars, stars), duck: old.duck || G.S.duckGot };
    if (p.fails) delete p.fails[key];
    Store.save();
  }
  SFX.play('win'); confetti();
  const tok = G.token = (G.token || 0) + 1, li = G.li;
  setTimeout(() => { if (G.token === tok && G.li === li && G.state === 'won' && !$('#game').hidden) showWin(stars, used); }, 1300);
}

// ---------- Effects ----------
export function burst(x: number, y: number, col: string, n: number, sp: number, kind?: string) {
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2, v = (0.5 + Math.random()) * sp;
    G.fx.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - sp * 0.5, life: 40 + Math.random() * 20, max: 60, col, r: 3 + Math.random() * 4, kind: kind || 'dot', rot: Math.random() * 6 });
  }
}
function confetti() {
  const cols = ['#FFC93C', '#FF5A5F', '#37C16B', '#7FD3FF', '#C9B6F2', '#FF8FC8'];
  for (let i = 0; i < 90; i++) G.fx.push({ x: Math.random() * W, y: -20 - Math.random() * 200, vx: (Math.random() - .5) * 3, vy: 2 + Math.random() * 3, life: 160, max: 160, col: cols[i % cols.length], r: 5 + Math.random() * 5, kind: 'conf', rot: Math.random() * 6 });
}

export function handleEvents() {
  const S = G.S; if (!S) return;
  let taps = 0;
  const fx: FxApi = { t: G.t, S, burst, sfx: SFX.play, particle: p => G.fx.push(p as Particle) };
  // the built-in events below always carry a position (and v where it is used)
  for (const ev of S.events.splice(0) as (SimEvent & { x: number; y: number; v: number })[]) {
    switch (ev.e) {
      case 'boing': SFX.play('boing'); burst(ev.x, ev.y + 10, '#FF7A7A', 6, 3); for (const h of S.heroes) if (Math.hypot(h.position.x - ev.x, h.position.y - ev.y) < 40) h.dizzy = 40; break;
      case 'thunk': SFX.play('thunk', ev.v); G.shake = Math.min(8, ev.v * 0.6); burst(ev.x, ev.y + 20, '#C9C3D6', 5, 2); break;
      case 'tap': if (taps++ < 2) SFX.play('tap', ev.v); break;
      case 'toast': SFX.play('toast'); break;
      case 'pop': SFX.play('pop'); burst(ev.x, ev.y - 40, '#E8B06A', 10, 3); break;
      case 'click': SFX.play('click2'); break;
      case 'snip': SFX.play('snip'); burst(ev.x, ev.y, '#C58B4A', 6, 2); break;
      case 'plop': SFX.play('plop'); burst(ev.x, ev.y - 40, '#FFF4DC', 12, 3); break;
      case 'splat': SFX.play('splat'); burst(ev.x, ev.y, '#FF5DB1', 14, 3); break;
      case 'creak': SFX.play('creak'); break;
      case 'win': { const gd = Registry.goals[S.goal!.type]; if (gd && gd.onWin) gd.onWin(S.goal!, fx); onWin(); break; }
      case 'fail': onFail(); break;
      case 'sfx': if (ev.s) SFX.play(ev.s as SoundType, ev.v); if (ev.col) burst(ev.x, ev.y, ev.col, ev.n || 8, 3); break;
      case 'shake': G.shake = ev.v || 6; break;
      default: { const f = Registry.fx(ev.e); if (f) f(ev, fx); }   // the kit's and the worlds' own events (def.fx)
    }
  }
  for (const h of S.heroes) if (h.speed > 11.5 && G.t - G.lastWiii > 140) { G.lastWiii = G.t; SFX.play('wiii'); }
  if (S.need > 1) updateHud();
}
